/**
 * TextField - Text input with label and error handling
 *
 * Use with React Hook Form:
 * <TextField label="Name" error={errors.name?.message} {...register('name')} />
 */

import { AlertCircle } from 'lucide-react';
import { forwardRef, type ReactNode, useId } from 'react';

import { BaseInput, type BaseInputProps } from '../base';
import { cn } from '../utils';

export interface TextFieldProps extends Omit<BaseInputProps, 'hasError' | 'iconRight'> {
  /** Field label */
  label: string;
  /** Optional element shown next to the label */
  labelAccessory?: ReactNode;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
  /** Error message from form validation */
  error?: string;
  /** Optional helper text shown below the input */
  hint?: string;
  /** Icon to display on the left side of the input */
  icon?: React.ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, labelAccessory, hideLabel, error, hint, icon, className, ...props }, ref) => {
    const inputId = useId();
    const labelClasses = cn(
      'text-sm font-medium text-gray-700 dark:text-zinc-300',
      hideLabel && 'sr-only'
    );

    return (
      <div className={className}>
        {labelAccessory && !hideLabel ? (
          <div className="flex items-center gap-2">
            <label htmlFor={inputId} className={labelClasses}>
              {label}
            </label>
            {labelAccessory}
          </div>
        ) : (
          <label htmlFor={inputId} className={labelClasses}>
            {label}
          </label>
        )}
        <BaseInput
          ref={ref}
          id={inputId}
          iconLeft={icon}
          iconRight={error ? <AlertCircle className="h-4 w-4 text-red-500" /> : undefined}
          hasError={!!error}
          className="mt-1"
          {...props}
        />
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {hint && !error && <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
      </div>
    );
  }
);

TextField.displayName = 'TextField';
